import { createMemo, createResource, createSignal, For, Show } from 'solid-js';
import { useApi, isOk } from '../api';
import type {
    ResourceAssignmentDto,
    ResourceAssignmentListResponse,
    RoleDefinitionDto,
    EffectiveActionDto,
    AccessAuditEntryDto,
} from '../api/generated/schemas';
import { PrincipalType, ScopeType, AccessAuditAction } from '../api/generated/schemas';
import Text from './Text';
import { showConfirm } from './Dialog';
import { useToast } from './MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { useUserContext } from './UserContextProvider';
import { can, actionLabelKey, roleNameKey } from '../services/resourceActions';
import { formatDateTime } from '../services/dateFormat';
import GrantAccessModal, { type GrantAccessValue } from './GrantAccessModal';
import styles from './ResourceAccessPanel.module.css';

// The grant flow uses <GrantAccessModal> rather than showDialog: a role name in a dropdown does not
// tell anyone what they are handing over, and this is the one screen in the product where that
// matters. showConfirm still covers revoking, which needs no explanation beyond the name.
export default function ResourceAccessPanel(props: { resourceId: string }) {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { userTenant } = useUserContext();

    const [reloadToken, setReloadToken] = createSignal(0);
    const [pending, setPending] = createSignal<string | null>(null);
    const [expanded, setExpanded] = createSignal<string | null>(null);
    const [grantMode, setGrantMode] = createSignal<'user' | 'tenant' | 'edit' | null>(null);
    const [editing, setEditing] = createSignal<ResourceAssignmentDto | null>(null);

    const [info] = createResource(
        () => ({ id: props.resourceId, token: reloadToken() }),
        async ({ id }) => {
            const res = await api.access.getAssignments(id);
            if (!isOk(res.data)) throw new Error(errorOf(res.data, t('ACCESS_LOAD_FAILED')));
            return res.data.value as ResourceAssignmentListResponse;
        });

    const [roles] = createResource(async () => {
        const res = await api.access.listRoles();
        return isOk(res.data) ? (res.data.value.roles ?? []) as RoleDefinitionDto[] : [];
    });

    const [why, setWhy] = createSignal<EffectiveActionDto[] | null>(null);

    // One principal can hold the same action through several assignments, typically as the
    // resource's owner and as a platform administrator. The endpoint reports every source on
    // purpose, but rendered flat that reads as duplicated rows, so sources collapse per action.
    const whyGrouped = createMemo(() => {
        const rows = why();
        if (!rows) return [];

        const byAction = new Map<string, string[]>();
        for (const e of rows) {
            const source = roleLabel(e.roleId, e.roleName)
                + (e.scopeType === ScopeType.NUMBER_2 ? '' : ` · ${t('ACCESS_INHERITED')}`);
            const sources = byAction.get(e.action) ?? [];
            if (!sources.includes(source)) sources.push(source);
            byAction.set(e.action, sources);
        }

        return [...byAction].map(([action, sources]) => ({ action, sources: sources.join(', ') }));
    });
    const [showHistory, setShowHistory] = createSignal(false);

    // A revoke deletes the assignment, so the history is the only place a removal is visible. Only
    // fetched when opened, since most visits to this panel are not audits.
    const [history] = createResource(
        () => (showHistory() ? { id: props.resourceId, token: reloadToken() } : null),
        async ({ id }) => {
            const res = await api.access.getAccessAudit(id, { take: 50 });
            return isOk(res.data) ? (res.data.value.entries ?? []) as AccessAuditEntryDto[] : [];
        });

    const auditLabel = (e: AccessAuditEntryDto) => {
        switch (e.action) {
            case AccessAuditAction.NUMBER_0: return t('ACCESS_LOG_GRANTED', e.principalName, e.newRoleName ?? '');
            case AccessAuditAction.NUMBER_1: return t('ACCESS_LOG_ROLE_CHANGED', e.principalName, e.previousRoleName ?? '', e.newRoleName ?? '');
            case AccessAuditAction.NUMBER_2: return t('ACCESS_LOG_EXPIRY_CHANGED', e.principalName);
            default: return t('ACCESS_LOG_REVOKED', e.principalName, e.previousRoleName ?? '');
        }
    };

    const family = () => info()?.resourceFamily ?? '';
    const canGrant = () => can(info()?.myActions, `${family()}.access.grant`);

    // Roles offered for this resource are the family's own. Platform administrator holds every
    // action in the catalog, so a prefix match would offer it here and grant far more than the
    // person picking it expects.
    const familyRoles = () => (roles() ?? []).filter(r =>
        r.actions.every(a => a.startsWith(`${family()}.`)));

    // Built-in role names are seeded in English; the ids are fixed, so the UI translates by id.
    const roleLabel = (roleId: string, fallback: string) => {
        const key = roleNameKey(roleId);
        return key ? t(key) : fallback;
    };

    const reload = () => setReloadToken(n => n + 1);

    function errorOf(env: unknown, fallback: string): string {
        const e = env as { errors?: Record<string, string[]>; message?: string };
        const first = e?.errors && Object.values(e.errors).flat()[0];
        // The last-administrator refusal answers with a bare message outside the envelope.
        return first ?? e?.message ?? fallback;
    }

    async function send(body: Record<string, unknown>, key: string, okMsg: string) {
        setPending(key);
        try {
            const res = await api.access.grantAccess(props.resourceId, body as never);
            if (isOk(res.data)) {
                toast?.info(okMsg);
                reload();
            } else {
                toast?.error(errorOf(res.data, t('ACCESS_GRANT_FAILED')));
            }
        } catch (e) {
            toast?.error(e instanceof Error ? e.message : t('ACCESS_GRANT_FAILED'));
        } finally {
            setPending(null);
        }
    }

    const tenantOptions = () => userTenant() ? [{
        label: userTenant()!.tenant?.name ?? t('ACCESS_TENANT'),
        value: userTenant()!.tenantId,
    }] : [];

    const openGrant = (principalType: PrincipalType) => {
        if (principalType === PrincipalType.NUMBER_1 && tenantOptions().length === 0) {
            toast?.error(t('ACCESS_NO_TEAMS'));
            return;
        }
        setEditing(null);
        setGrantMode(principalType === PrincipalType.NUMBER_1 ? 'tenant' : 'user');
    };

    const openEdit = (a: ResourceAssignmentDto) => {
        setEditing(a);
        setGrantMode('edit');
    };

    const closeModal = () => { setGrantMode(null); setEditing(null); };

    const submitModal = async (v: GrantAccessValue) => {
        const target = editing();
        const body = target
            ? {
                principalType: target.principalType,
                // The identifier we already hold, never the display name: a rename between load
                // and save would otherwise turn a role change into "user not found".
                principalId: target.principalId,
                roleId: v.roleId,
                expiresAt: v.expiresAt,
                clearExpiry: v.clearExpiry,
            }
            : {
                principalType: grantMode() === 'tenant' ? PrincipalType.NUMBER_1 : PrincipalType.NUMBER_0,
                username: v.username,
                tenantId: v.tenantId,
                roleId: v.roleId,
                expiresAt: v.expiresAt,
            };

        const before = reloadToken();
        await send(body, target?.id ?? 'new', target ? t('ACCESS_UPDATED') : t('ACCESS_GRANTED'));
        // send() bumps the token only when the save succeeded, so a failure keeps the modal open
        // with the entered values intact.
        if (reloadToken() !== before) closeModal();
    };

    const revoke = async (a: ResourceAssignmentDto) => {
        if (!await showConfirm(t('ACCESS_REVOKE_CONFIRM', a.principalName), true)) return;

        setPending(a.id);
        try {
            const res = await api.access.revokeAccess(props.resourceId, a.id);
            if (isOk(res.data)) {
                toast?.info(t('ACCESS_REVOKED'));
                reload();
            } else {
                toast?.error(errorOf(res.data, t('ACCESS_REVOKE_FAILED')));
            }
        } catch (e) {
            toast?.error(e instanceof Error ? e.message : t('ACCESS_REVOKE_FAILED'));
        } finally {
            setPending(null);
        }
    };

    const toggleWhy = async (a: ResourceAssignmentDto) => {
        if (expanded() === a.id) { setExpanded(null); return; }
        setExpanded(a.id);
        setWhy(null);
        const res = await api.access.getEffectiveAccess(props.resourceId, { principalId: a.principalId });
        if (isOk(res.data)) setWhy((res.data.value.actions ?? []) as EffectiveActionDto[]);
    };

    const scopeLabel = (a: ResourceAssignmentDto) =>
        a.scope === ScopeType.NUMBER_0 ? t('ACCESS_VIA_PLATFORM')
            : a.scope === ScopeType.NUMBER_1 ? t('ACCESS_VIA_TEAM', a.scopeName ?? '')
                : null;

    return (
        <div class="v" style={{ gap: 'var(--spacing-lg)' }}>
            <Show when={info.state !== 'pending'} fallback={
                <div class={styles.skeleton}>
                    <div class={styles.skeletonRow} />
                    <div class={styles.skeletonRow} />
                </div>
            }>
                <Show when={info.state !== 'errored'} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-triangle-exclamation" />
                        <p>{(info.error as Error)?.message ?? t('ACCESS_LOAD_FAILED')}</p>
                        <button class="btn btn-secondary btn-sm" onClick={reload}>
                            <i class="fa-solid fa-rotate" /> <Text val="RETRY" />
                        </button>
                    </div>
                }>
                    <div class={styles.ownerRow}>
                        <i class="fa-solid fa-crown" />
                        <span class={styles.ownerName}>{info()!.ownerName}</span>
                        <span class={styles.ownerLabel}><Text val="ACCESS_OWNER" /></span>
                    </div>

                    <Show when={canGrant()}>
                        <div class={styles.actions}>
                            <button class="btn btn-primary btn-sm" disabled={!!pending()} onClick={() => openGrant(PrincipalType.NUMBER_0)}>
                                <i class="fa-solid fa-user-plus" /> <Text val="ACCESS_SHARE_WITH_USER" />
                            </button>
                            <button class="btn btn-secondary btn-sm" disabled={!!pending()} onClick={() => openGrant(PrincipalType.NUMBER_1)}>
                                <i class="fa-solid fa-building" /> <Text val="ACCESS_SHARE_WITH_TEAM" />
                            </button>
                        </div>
                    </Show>

                    <Show when={info()!.assignments.length > 0} fallback={
                        <div class={styles.empty}>
                            <i class="fa-solid fa-user-group" />
                            <p><Text val="ACCESS_NOT_SHARED" /></p>
                        </div>
                    }>
                        <ul class={styles.list}>
                            <For each={info()!.assignments}>
                                {(a) => (
                                    <li class={styles.row} classList={{ [styles.rowInherited]: a.scope !== ScopeType.NUMBER_2 }}>
                                        <div class={styles.rowMain}>
                                            <i class={a.principalType === PrincipalType.NUMBER_1 ? 'fa-solid fa-building' : 'fa-solid fa-user'} />
                                            <div>
                                                <div class={styles.rowName}>
                                                    {a.principalName}
                                                    <Show when={a.isOwner}>
                                                        <span class={styles.tag}><Text val="ACCESS_OWNER" /></span>
                                                    </Show>
                                                    <Show when={a.isExpired}>
                                                        <span class={`${styles.tag} ${styles.tagExpired}`}><Text val="ACCESS_EXPIRED" /></span>
                                                    </Show>
                                                </div>
                                                <div class={styles.rowSub}>
                                                    {roleLabel(a.roleId, a.roleName)}
                                                    <Show when={scopeLabel(a)}>
                                                        {' '}<span class={styles.scope}>{scopeLabel(a)}</span>
                                                    </Show>
                                                    <Show when={a.expiresAt}>
                                                        {' '}<span class={a.isExpired ? styles.expired : styles.expiry}>
                                                            <Text val="ACCESS_UNTIL" /> {formatDateTime(a.expiresAt!)}
                                                        </span>
                                                    </Show>
                                                    <Show when={a.createdByName}>
                                                        {' '}<span class={styles.grantedBy}>
                                                            {t('ACCESS_GRANTED_BY', a.createdByName!, formatDateTime(a.createdAt))}
                                                        </span>
                                                    </Show>
                                                </div>
                                            </div>
                                        </div>

                                        <div class={styles.rowActions}>
                                            <button class="btn btn-secondary btn-icon btn-sm" title={t('ACCESS_WHY')}
                                                aria-label={t('ACCESS_WHY')} aria-expanded={expanded() === a.id}
                                                onClick={() => toggleWhy(a)}>
                                                <i class="fa-solid fa-circle-question" />
                                            </button>
                                            {/* Inherited rows are managed where they were granted. Owner rows are
                                                editable: a resource can have several owners, which is how a
                                                transfer happens without a gap, and what keeps it administrable is
                                                the last-administrator rule on save, not hiding these buttons. */}
                                            <Show when={canGrant() && a.scope === ScopeType.NUMBER_2}>
                                                <button class="btn btn-secondary btn-icon btn-sm" disabled={pending() === a.id}
                                                    title={t('ACCESS_EDIT')} aria-label={t('ACCESS_EDIT')}
                                                    onClick={() => openEdit(a)}>
                                                    <i class={pending() === a.id ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-shield'} />
                                                </button>
                                                <button class="btn btn-danger btn-icon btn-sm" disabled={pending() === a.id}
                                                    title={t('ACCESS_REVOKE')} aria-label={t('ACCESS_REVOKE')}
                                                    onClick={() => revoke(a)}>
                                                    <i class="fa-solid fa-trash" />
                                                </button>
                                            </Show>
                                        </div>

                                        <Show when={expanded() === a.id}>
                                            <div class={styles.why}>
                                                <Show when={why()} fallback={<i class={`fa-solid fa-spinner fa-spin ${styles.whyLoading}`} />}>
                                                    <Show when={whyGrouped().length > 0} fallback={
                                                        <span class={styles.rowSub}><Text val="ACCESS_WHY_NONE" /></span>
                                                    }>
                                                        <For each={whyGrouped()}>
                                                            {(e) => (
                                                                <div class={styles.whyRow} title={e.action}>
                                                                    <span>{t(actionLabelKey(e.action))}</span>
                                                                    <span class={styles.rowSub}>{e.sources}</span>
                                                                </div>
                                                            )}
                                                        </For>
                                                    </Show>
                                                </Show>
                                            </div>
                                        </Show>
                                    </li>
                                )}
                            </For>
                        </ul>
                    </Show>

                    <div class={styles.historyHeader}>
                        <button class="btn btn-secondary btn-sm" aria-expanded={showHistory()}
                            onClick={() => setShowHistory(!showHistory())}>
                            <i class={`fa-solid ${showHistory() ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                            {' '}<Text val="ACCESS_HISTORY" />
                        </button>
                    </div>

                    <Show when={showHistory()}>
                        <Show when={history.state !== 'pending'} fallback={
                            <div class={styles.skeleton}><div class={styles.skeletonRow} /></div>
                        }>
                            <Show when={(history() ?? []).length > 0} fallback={
                                <div class={styles.empty}>
                                    <i class="fa-solid fa-clock-rotate-left" />
                                    <p><Text val="ACCESS_HISTORY_EMPTY" /></p>
                                </div>
                            }>
                                <ul class={styles.list}>
                                    <For each={history()!}>
                                        {(e) => (
                                            <li class={styles.logRow}>
                                                <i class={`fa-solid ${e.action === AccessAuditAction.NUMBER_3 ? 'fa-user-minus' : 'fa-user-shield'}`} />
                                                <div>
                                                    <div class={styles.rowName}>{auditLabel(e)}</div>
                                                    <div class={styles.rowSub}>
                                                        {t('ACCESS_LOG_BY', e.actorName, formatDateTime(e.createdAt))}
                                                    </div>
                                                </div>
                                            </li>
                                        )}
                                    </For>
                                </ul>
                            </Show>
                        </Show>
                    </Show>
                </Show>
            </Show>

            <GrantAccessModal
                open={grantMode() !== null}
                mode={grantMode() ?? 'user'}
                roles={familyRoles()}
                tenants={tenantOptions()}
                existing={editing() ? {
                    principalName: editing()!.principalName,
                    roleId: editing()!.roleId,
                    expiresAt: editing()!.expiresAt ?? null,
                } : undefined}
                busy={!!pending()}
                onClose={closeModal}
                onSubmit={submitModal} />
        </div>
    );
}
